import Close from "@mui/icons-material/Close";
import FolderOutlined from "@mui/icons-material/FolderOutlined";
import PermMediaOutlined from "@mui/icons-material/PermMediaOutlined";
import { Box, Button, IconButton, MenuItem, TextField, Tooltip, Typography } from "@mui/material";
import { truncate } from "lodash-es";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { useAsyncStateSupabaseCallback } from "../../utils/hooks/use-async-state-callback";
import { useFolders } from "./hooks/use-folders";
import { SupabaseFolderService } from "./server/folder-service";
import { ROOT_ID, flattenTreeItems, folderNameFromId, transformFoldersToTreeItems } from "./utils";

import type { UUID } from "../../utils/uuid";
import type { UiFolderId } from "./utils";
import type { FC } from "react";

interface AssetFolderSelectProps {
  selectedFolderId: UiFolderId;
  onChange: (folderId: UiFolderId) => void;
  onShowNewFolderFieldChange?: (showNewFolderField: boolean) => void;
}

export const AssetFolderSelect: FC<AssetFolderSelectProps> = ({
  selectedFolderId,
  onChange,
  onShowNewFolderFieldChange,
}) => {
  const { data: folders, loading, error: foldersError, reload } = useFolders();
  const [showNewFolderField, setShowNewFolderField] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const treeItems = useMemo(() => transformFoldersToTreeItems(folders ?? []), [folders]);
  const options = useMemo(() => flattenTreeItems(treeItems), [treeItems]);
  const selectedLabel = folderNameFromId(selectedFolderId, folders ?? null) ?? "Unknown folder";
  const normalizedNewFolderName = newFolderName.trim();

  useEffect(() => {
    onShowNewFolderFieldChange?.(showNewFolderField);
  }, [onShowNewFolderFieldChange, showNewFolderField]);

  const hasDuplicateFolderName = useMemo(() => {
    if (!normalizedNewFolderName || !folders) return false;
    const parentFolderId = selectedFolderId === ROOT_ID ? null : selectedFolderId;
    return folders.some(
      (folder) =>
        folder.parent_folder_id === parentFolderId &&
        folder.name.trim() === normalizedNewFolderName,
    );
  }, [folders, normalizedNewFolderName, selectedFolderId]);

  const menuOptions = useMemo(() => {
    if (options.some((option) => option.id === selectedFolderId)) return options;
    return [{ id: selectedFolderId, label: selectedLabel, depth: 0 }, ...options];
  }, [options, selectedFolderId, selectedLabel]);

  const { loading: savingNewFolder, load: saveNewFolder } = useAsyncStateSupabaseCallback(
    async (
      { supabase },
      { name, parentFolderId }: { name: string; parentFolderId: UUID | null },
    ) => {
      const service = new SupabaseFolderService(supabase);
      const { data, error: newFolderError } = await service.insert({
        parent_folder_id: parentFolderId,
        name,
      });
      if (newFolderError) throw newFolderError;
      return data;
    },
    [],
  );

  const handleCreateFolder = (): void => {
    if (normalizedNewFolderName.length === 0 || hasDuplicateFolderName) return;
    const parentFolderId = selectedFolderId === ROOT_ID ? null : selectedFolderId;
    void saveNewFolder({
      name: normalizedNewFolderName,
      parentFolderId,
    })
      .then((createdFolder) => {
        if (!createdFolder) return;
        toast.success(`Folder '${createdFolder.name}' created`);
        setNewFolderName("");
        setShowNewFolderField(false);
        reload();
        onChange(createdFolder.id);
      })
      .catch((error) => {
        console.error(error);
        if (error instanceof Error && error.message.includes("folder_unique_name")) {
          toast.error("Folder name must be unique");
        } else {
          toast.error("Error when creating folder");
        }
      });
  };

  return (
    <Box display="flex" flexDirection="column">
      <TextField
        select
        label="Save to folder"
        value={selectedFolderId}
        // oxlint-disable-next-line typescript/no-unsafe-type-assertion
        onChange={(event) => onChange(event.target.value as UiFolderId)}
        disabled={loading || Boolean(foldersError)}
        error={Boolean(foldersError)}
        helperText={foldersError ? "Unable to load folders" : null}
        slotProps={{
          select: {
            renderValue: () => selectedLabel,
            MenuProps: {
              PaperProps: {
                sx: { maxHeight: 360 },
              },
            },
          },
        }}
      >
        {menuOptions.map((option) => {
          const isRoot = option.id === ROOT_ID;
          return (
            <MenuItem key={option.id} value={option.id}>
              <Box display="flex" alignItems="center" gap={1} sx={{ pl: option.depth * 2 }}>
                {isRoot ? (
                  <PermMediaOutlined fontSize="small" />
                ) : (
                  <FolderOutlined fontSize="small" />
                )}
                <Tooltip title={option.label.length > 50 ? option.label : ""}>
                  <Typography variant="body2" noWrap>
                    {truncate(option.label, { length: 50 })}
                  </Typography>
                </Tooltip>
              </Box>
            </MenuItem>
          );
        })}
      </TextField>
      <Box display="flex" alignItems="center" gap={0.5} sx={{ alignSelf: "flex-start", mt: 1 }}>
        <Button
          onClick={() => {
            setNewFolderName("");
            setShowNewFolderField(true);
          }}
          variant="text"
          size="small"
          data-analytics-id="upload-file-create-new-folder"
          disabled={showNewFolderField}
          sx={{
            p: 0,
            minHeight: "auto",
            textTransform: "none",
            lineHeight: 1.5,
          }}
        >
          <Typography variant="body2">
            {`Create new folder in '${truncate(selectedLabel, { length: 24 })}'`}
          </Typography>
        </Button>
        {showNewFolderField ? (
          <Tooltip title="Cancel new folder creation">
            <IconButton
              size="small"
              data-analytics-id="upload-file-create-new-folder-cancel"
              onClick={() => {
                setShowNewFolderField(false);
                setNewFolderName("");
              }}
              aria-label="Cancel new folder creation"
            >
              <Close fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : null}
      </Box>
      {showNewFolderField ? (
        <Box display="flex" alignItems="center" gap={1} sx={{ mt: 2 }}>
          <TextField
            fullWidth
            label="New folder name"
            value={newFolderName}
            onChange={(event) => setNewFolderName(event.target.value)}
            error={hasDuplicateFolderName}
            helperText={
              hasDuplicateFolderName
                ? `A folder with this name already exists in ${selectedLabel}`
                : null
            }
          />
          <Button
            onClick={handleCreateFolder}
            variant="contained"
            data-analytics-id="upload-file-create-new-folder-confirm"
            disabled={
              savingNewFolder || normalizedNewFolderName.length === 0 || hasDuplicateFolderName
            }
          >
            Create
          </Button>
        </Box>
      ) : null}
    </Box>
  );
};
